/**
 * Community Hub Query Hooks
 *
 * React Query hooks for fetching Community Hub data
 */

import { useQuery } from '@tanstack/react-query';
import {
  getTermsStatus,
  getPosts,
  getPost,
  getComments,
  getReportReasons,
  getReports,
  getBlockedUsers,
} from '../services/communityApi';
import type { PostFilters, CommentFilters, ReportFilters } from '../types/community';

// Query keys for caching
export const communityKeys = {
  all: ['community'] as const,
  // Terms
  terms: () => [...communityKeys.all, 'terms'] as const,
  termsStatus: () => [...communityKeys.terms(), 'status'] as const,
  // Posts
  posts: () => [...communityKeys.all, 'posts'] as const,
  postsList: (filters?: PostFilters) => [...communityKeys.posts(), 'list', filters] as const,
  postsDetail: () => [...communityKeys.posts(), 'detail'] as const,
  postDetail: (id: string) => [...communityKeys.postsDetail(), id] as const,
  // Comments
  comments: () => [...communityKeys.all, 'comments'] as const,
  postComments: (postId: string, filters?: CommentFilters) =>
    [...communityKeys.comments(), 'post', postId, filters] as const,
  // Reports
  reports: () => [...communityKeys.all, 'reports'] as const,
  reportReasons: () => [...communityKeys.reports(), 'reasons'] as const,
  reportsList: (filters?: ReportFilters) => [...communityKeys.reports(), 'list', filters] as const,
  // Blocks
  blocks: () => [...communityKeys.all, 'blocks'] as const,
  blocksList: (page?: number, limit?: number) =>
    [...communityKeys.blocks(), 'list', { page, limit }] as const,
};

// ============================================
// TERMS HOOKS
// ============================================

/**
 * Hook to check user's terms acceptance status
 */
export const useTermsStatus = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: communityKeys.termsStatus(),
    queryFn: getTermsStatus,
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// ============================================
// POSTS HOOKS
// ============================================

/**
 * Hook to fetch community feed posts
 */
export const usePosts = (filters?: PostFilters, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: communityKeys.postsList(filters),
    queryFn: () => getPosts(filters),
    enabled: options?.enabled ?? true,
  });
};

/**
 * Hook to fetch a single post
 */
export const usePost = (id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: communityKeys.postDetail(id),
    queryFn: () => getPost(id),
    enabled: options?.enabled ?? !!id,
  });
};

// ============================================
// COMMENTS HOOKS
// ============================================

/**
 * Hook to fetch comments for a post
 */
export const usePostComments = (
  postId: string,
  filters?: CommentFilters,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: communityKeys.postComments(postId, filters),
    queryFn: () => getComments(postId, filters),
    enabled: options?.enabled ?? !!postId,
  });
};

// ============================================
// REPORT HOOKS
// ============================================

/**
 * Hook to fetch report reasons
 */
export const useReportReasons = () => {
  return useQuery({
    queryKey: communityKeys.reportReasons(),
    queryFn: getReportReasons,
    staleTime: Infinity, // Never stale - these don't change
  });
};

/**
 * Hook to fetch reports (Super Admin)
 */
export const useReports = (filters?: ReportFilters, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: communityKeys.reportsList(filters),
    queryFn: () => getReports(filters),
    enabled: options?.enabled ?? true,
  });
};

// ============================================
// BLOCKS HOOKS
// ============================================

/**
 * Hook to fetch blocked users (Super Admin)
 */
export const useBlockedUsers = (
  page?: number,
  limit?: number,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: communityKeys.blocksList(page, limit),
    queryFn: () => getBlockedUsers(page, limit),
    enabled: options?.enabled ?? true,
  });
};

// Default export
export default {
  useTermsStatus,
  usePosts,
  usePost,
  usePostComments,
  useReportReasons,
  useReports,
  useBlockedUsers,
};
